/**
 * The home screen: finding an organization when there is no QR code (GH-110).
 *
 * The QR path is still the primary one and this screen does not compete with it — it exists for
 * the donor who heard about a campaign, or who is standing in a hall and never scanned anything.
 *
 * ## Location
 *
 * No prompt ever appears on load. The browser's permission prompt appears only after the donor
 * taps "Use my location", and the sentence explaining why sits next to the button before they do
 * (SPEC.md §6.2). A page that prompts the instant it opens teaches people to dismiss prompts, and
 * this is a page about trust.
 *
 * A donor who has already granted permission is a different case: the choice was made, on this
 * page, with the explanation in front of them. For them the position is read silently on load
 * (the Permissions API says `granted`, so no prompt is possible) and the list opens sorted by
 * distance — the organization down the road should not rank below a demo record half a country
 * away just because the sort forgot what the donor already answered.
 *
 * If they decline, or the browser has no geolocation, or it times out — all ordinary — the screen
 * silently becomes the search screen, which was always available anyway.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  MAX_DIRECTORY_RESULTS,
  NEAREST_RESULT_COUNT,
  type DirectoryEntry,
  type DonorSessionResponse,
} from "@givehere/contracts";
import { fetchNearby, requestApproximatePosition, searchOrganizations } from "./api.js";
import { isHappening, splitDirectory } from "./directory-sections.js";
import { DonorPanel } from "./DonorPanel.js";
import { fetchDonorSession, forgetToken, storedToken } from "./donor-session.js";
import { LocationIcon, MenuIcon, PersonIcon, SearchIcon, TicketIcon } from "./icons.js";
import { Explain } from "./Explain.js";
import { DonorPledges } from "./DonorPledges.js";
import { EventsForMembers } from "./EventAccess.js";
import { HomeInviteCard, WelcomeLockup } from "./HomeInviteCard.js";
import { useMembershipPulse } from "./membership-hooks.js";
import { NearbyExplainer } from "./NearbyExplainer.js";
import { OrgMark } from "./OrgMark.js";
import { UpcomingEvents } from "./UpcomingEvents.js";

type Position = { latitude: number; longitude: number };

const SHOW_MORE_COUNT = 12;

export function Home() {
  const [results, setResults] = useState<DirectoryEntry[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [mode, setMode] = useState<"nearby" | "search" | "browse">("browse");
  /**
   * With a position, the list opens showing only what is within about a mile — the server marks
   * each row (`withinNearbyRadius`) so this bundle never handles a raw distance. "Show more"
   * flips this and reveals everything fetched, still sorted by distance.
   */
  const [expanded, setExpanded] = useState(false);
  /**
   * Whether the donor has asked to see past the events (owner-directed 2026-09-16: *"first page
   * … should always be events only. Show orgs only if show more is pressed"*).
   *
   * The organizations half of a split list is drawn only once this is set; before that the screen
   * is what is on, and one button. Like `expanded`, it is never unset: a list that shrinks under
   * the thumb that grew it is a list people stop trusting.
   */
  const [showOrganizations, setShowOrganizations] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(true);
  const [locating, setLocating] = useState(false);
  /** The pre-permission sheet, shown before the browser is asked anything. */
  const [explainingNearby, setExplainingNearby] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * GH-871b: the server withheld events from this list (membership on, not a member). The rows
   * are organizations only, and the one "Events are for members" line stands where events would.
   */
  const [eventsHidden, setEventsHidden] = useState(false);
  /** "Have an account? Sign in" on the invite card opens the sign-in door, not the event picker. */
  const [signInDirect, setSignInDirect] = useState(false);

  /**
   * The remembered donor, if any. Loaded quietly: a dead or missing token renders the same
   * signed-out icon, never an error — being unremembered is a normal state, not a failure.
   */
  const [donorToken, setDonorToken] = useState<string | null>(null);
  const [donorSession, setDonorSession] = useState<DonorSessionResponse | null>(null);
  /** Which door into the donor sheet is open: the person icon or the hamburger menu. */
  const [panel, setPanel] = useState<"profile" | "menu" | null>(null);

  /**
   * Arriving from an event page's "join the room" link: the visitor came to participate, not
   * to browse the directory, so the donor sheet opens itself on that event. Consumed once —
   * the parameter is stripped from the URL so a reload or a shared link doesn't trap the next
   * reader in a sheet they never asked for.
   */
  const [joinEventId, setJoinEventId] = useState<string | null>(() => {
    const param = new URLSearchParams(window.location.search).get("join");
    if (!param || !param.trim()) return null;
    const url = new URL(window.location.href);
    url.searchParams.delete("join");
    window.history.replaceState(null, "", url.toString());
    return param.trim();
  });
  const [sessionProbeSettled, setSessionProbeSettled] = useState(false);
  /**
   * GH-879: while membership is on (the pulse answers) and nobody is signed in, the home is the
   * invite-only welcome — the lockup in place of the title, then the code and the waitlist. Waits
   * for the remembered-donor probe so a signed-in donor never sees it flash.
   */
  const pulse = useMembershipPulse();
  const welcome = sessionProbeSettled && !donorSession && pulse !== undefined && pulse !== null;

  useEffect(() => {
    const token = storedToken();
    if (!token) {
      setSessionProbeSettled(true);
      return;
    }
    let cancelled = false;
    fetchDonorSession(token)
      .then((session) => {
        if (cancelled) return;
        setDonorToken(token);
        setDonorSession(session);
      })
      .catch(() => {
        // Expired or revoked: forget it so the next open starts clean.
        if (!cancelled) forgetToken();
      })
      .finally(() => {
        if (!cancelled) setSessionProbeSettled(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The join link waits for the remembered-donor probe: opening the sheet before we know who
  // this is would ask a returning donor for their phone number all over again.
  useEffect(() => {
    // A join link is about participating, so it opens the menu face of the sheet.
    if (joinEventId && sessionProbeSettled) setPanel("menu");
  }, [joinEventId, sessionProbeSettled]);

  /**
   * Monotonic id per load, so the list belongs to the newest request. The unsorted first paint
   * and the silent sorted-by-granted-permission load run concurrently on mount; without this,
   * whichever response is slower would win, and slower is sometimes the stale one.
   */
  const loadSequence = useRef(0);

  const load = useCallback(async (at: Position | null, limit: number) => {
    const sequence = ++loadSequence.current;
    setBusy(true);
    setError(null);
    try {
      const response = await fetchNearby(at, limit);
      if (sequence !== loadSequence.current) return;
      setEventsHidden(response.eventsHidden === true);
      setResults(response.results);
      setHasMore(response.hasMore);
      setMode(response.mode);
    } catch {
      if (sequence === loadSequence.current) {
        setError("Could not load the directory.");
      }
    } finally {
      if (sequence === loadSequence.current) setBusy(false);
    }
  }, []);

  useEffect(() => {
    void load(null, NEAREST_RESULT_COUNT);
  }, [load]);

  /**
   * Already granted? Then sort by distance without waiting for the tap. Guarded by the
   * Permissions API: this path runs only when the state is `granted`, where reading the position
   * shows no prompt. Everywhere else — `prompt`, `denied`, or a browser without the API — nothing
   * happens and the button remains the only way in. The unsorted list above renders first either
   * way, so a slow fix re-orders a visible page rather than blanking it.
   */
  useEffect(() => {
    if (!("permissions" in navigator)) return;
    let cancelled = false;
    navigator.permissions
      .query({ name: "geolocation" })
      .then(async (status) => {
        if (cancelled || status.state !== "granted") return;
        const fix = await requestApproximatePosition();
        if (cancelled || !fix) return;
        setPosition(fix);
        setExpanded(false);
        // A fuller page than the on-screen default: the ~1 mile filter below needs the rows
        // beyond the radius already in hand, so "Show more" reveals instantly instead of
        // round-tripping. What the donor first *sees* is still only what is close.
        await load(fix, SHOW_MORE_COUNT);
      })
      // A Permissions API hiccup just means the tap-to-sort flow, same as before.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [load]);

  /**
   * Debounced so a fast typist does not fire a request per keystroke, and so the list does not
   * flicker through partial-word results while they are still typing.
   */
  useEffect(() => {
    const text = query.trim();
    if (text === "") return;

    const timer = setTimeout(() => {
      setBusy(true);
      searchOrganizations(text, position)
        .then((response) => {
          setEventsHidden(response.eventsHidden === true);
          setResults(response.results);
          setHasMore(response.hasMore);
          setMode("search");
        })
        .catch(() => setError("We could not search right now."))
        .finally(() => setBusy(false));
    }, 250);

    return () => clearTimeout(timer);
  }, [query, position]);

  const useMyLocation = async () => {
    setLocating(true);
    const fix = await requestApproximatePosition();
    setLocating(false);
    if (!fix) {
      // Not an error: declining is a legitimate choice and search still works.
      setError(null);
      return;
    }
    setPosition(fix);
    setQuery("");
    setExpanded(false);
    await load(fix, SHOW_MORE_COUNT);
  };

  const heading =
    mode === "search" ? "Results" : mode === "nearby" ? "Nearest you" : "All organizations";

  /**
   * The default nearby view is "within about a mile", because a donor standing in a
   * neighbourhood wants the organization down the road, not a ranked tour of the county. When
   * nothing at all is that close, hiding everything would render an empty directory — so the view falls
   * back to the classic nearest-three, and "Show more" still reveals the rest.
   */
  const withinRadius = results.filter((entry) => entry.withinNearbyRadius);
  const visible =
    mode === "nearby" && !expanded
      ? withinRadius.length > 0
        ? withinRadius
        : results.slice(0, NEAREST_RESULT_COUNT)
      : results;
  const hiddenCount = results.length - visible.length;

  /**
   * Events above, organizations below (owner-directed 2026-09-01).
   *
   * `isSplit` is read off the rows rather than off `mode`, because either half is empty for
   * ordinary reasons — a town with nothing on tonight is all organizations — and a heading over
   * nothing says something untrue about what follows it. When only one half has rows the screen
   * draws exactly the single labelled list it always did.
   */
  const { happening, organizations } = splitDirectory(visible, mode !== "search");
  const isSplit = happening.length > 0 && organizations.length > 0;
  /**
   * Split, and not yet asked for. A town with nothing on tonight is all organizations and draws
   * them at once — an empty first screen with a "Show more" under it would be a directory hiding
   * everything it has.
   */
  const hidesOrganizations = isSplit && !showOrganizations;

  /**
   * One sentence under the tier label, and no distance on any row — `ADDENDUM.md` screen 2. The
   * two halves are one rule: how far "near" reaches is a fact about the list, so it is stated once
   * at its head rather than re-stated beside every name, where it would be a readout of the
   * reader's own position at a precision the input never had (the fix is coarsened to a ~1 km grid
   * before it is sent, SPEC.md §6.2).
   *
   * Three sentences and not one, because this view has three states the heading alone cannot tell
   * apart. The fallback especially: when nothing at all is within the radius the list silently
   * becomes the nearest few, and "Nearest you" reads identically over both — a donor could not
   * tell "three organizations near you" from "nothing is near you, here are the closest three".
   *
   * The addendum's own wording ("Within a mile of you") holds *here* and is wrong on iOS, because
   * the two surfaces ask different questions: this one filters on `withinNearbyRadius`
   * (`NEARBY_RADIUS_KM`, 1.7 km) with no event requirement, while home on iOS asks for events
   * inside `NEARBY_EVENT_RADIUS_KM` (five miles). Each surface states what is true of its own
   * list; the divergence itself predates this change and is recorded rather than papered over.
   */
  /*
   * Shortened 2026-08-31 to the owner's five-word rule — and the second branch lost the clause
   * that made it different from the first ("— the closest few"). That clause was the whole point
   * of having two strings, so it does not simply vanish: "Nothing within a mile" is now the
   * distinguishing half on its own, and it still answers the question the long pair existed to
   * answer, which is whether this list is *near* or merely *nearest*.
   */
  const headingNote =
    mode !== "nearby" || visible.length === 0
      ? null
      : expanded
        ? "Closest first."
        : withinRadius.length > 0
          ? "Within about a mile"
          : "Nothing within a mile";

  return (
    <main className="card card--home lattice-edge">
      {/*
        A bar, then a title — not a title with two controls parked on top of it.

        The controls used to be `position: absolute` at the corners of a centred heading that
        reserved no room for them, so the rendered glyphs of "Who are you giving to?" ran under
        both at *every* width measured, desktop included: 310px of title inside a ~368px column
        with a 44px control on one corner and a ~102px pill on the other. Two tap targets sat on
        the words, and a tap near either end of the title hit a button.

        It is a row now, which is also what iOS has always done — `menuButton` and `donorPill` are
        `ToolbarContent`, so they occupy a navigation bar and the large title sits *below* them and
        can never collide. `ADDENDUM.md` screen 1 describes exactly this ("Header on all seven:
        hamburger … and the dual control"), and the web was imitating its look with overlay
        positioning rather than building it.
      */}
      <header className="home__head">
        <div className="home__bar">
          {/* What's on: reactions, polls, and the quiet site links. */}
          <button
            type="button"
            className="home__profile home__profile--menu"
            aria-label="Menu"
            onClick={() => setPanel("menu")}
          >
            <MenuIcon />
          </button>
          {/*
            Two drawings, and the asymmetry is deliberate. ADDENDUM.md screen 1 asks for a pill
            reading "Check in" for a stranger and the donor's initials once they are known, *and*
            for a control that never reveals which meaning it has. Those cannot both hold, so we
            keep the half that protects somebody: "Check in" says nothing about who is holding the
            phone, while initials over a directory of houses of worship are most of an
            identification. A signed-in donor keeps the anonymous glyph, and the dot says "you are
            remembered" without putting a name on a screen that might be face-up on a table.
          */}
          {donorSession ? (
            <button
              type="button"
              className="home__profile home__profile--in"
              aria-label="Your profile"
              onClick={() => setPanel("profile")}
            >
              <PersonIcon />
              <span className="home__profile-dot" aria-hidden="true" />
            </button>
          ) : (
            <button
              type="button"
              className="home__checkin"
              /* Fuller than the visible label, and starting with it, so a spoken name says what a
                 sighted donor cannot infer from two words while WCAG 2.5.3 still holds. */
              aria-label="Check in at an event"
              onClick={() => setPanel("profile")}
            >
              <TicketIcon />
              Check in
            </button>
          )}
        </div>

        {welcome ? (
          <WelcomeLockup />
        ) : (
          <>
            <h1 className="home__title">Who are you giving to?</h1>
            <p className="home__subtitle">All verified nonprofits</p>
          </>
        )}
      </header>

      {/*
        GH-871b, recomposed by GH-879 (owner, 2026-10-04): signed out, while membership is on, the
        code is the most prominent thing on the page and the waitlist sits under it. The directory
        stays below — on the web it is the only way to an organization, and giving is never behind
        a code. "Check in" in the bar is the way in with an event's code.
      */}
      {welcome && (
        <HomeInviteCard
          onSignIn={() => {
            setSignInDirect(true);
            setPanel("profile");
          }}
          onIssued={(issued) => {
            setDonorToken(issued.token);
            setDonorSession(issued);
          }}
          onNotice={setError}
        />
      )}

      <div className="home__search">
        <span className="home__search-icon" aria-hidden="true">
          <SearchIcon />
        </span>
        <label className="visually-hidden" htmlFor="directory-search">
          Name, city, or ZIP
        </label>
        <input
          id="directory-search"
          type="search"
          autoComplete="off"
          placeholder="Name, city, or ZIP"
          value={query}
          onChange={(changed) => setQuery(changed.target.value)}
        />
      </div>

      {/*
        One line. The sentence that used to hang under it — what is sent, and how coarsely — has
        not been deleted; it moved into the sheet this opens, which is where ADDENDUM.md screen 5
        puts it. It still precedes anything leaving the device, because the browser has not been
        asked while that sheet is up, and the directory gets back the room a paragraph was taking
        from the list a donor came here to read.
      */}
      {!position && (
        <button
          type="button"
          className="home__locate"
          onClick={() => setExplainingNearby(true)}
          disabled={locating}
        >
          <LocationIcon />
          <span>{locating ? "Finding you…" : "See what's near you"}</span>
        </button>
      )}

      {explainingNearby && (
        <NearbyExplainer
          onAllow={() => {
            setExplainingNearby(false);
            void useMyLocation();
          }}
          /*
            Nothing is recorded and nothing is asked again. ADDENDUM.md screen 6: the sheet closes
            and the screen *is* the search screen — no warning colour, no banner, no retry, no
            second ask. Re-entry is only ever donor-initiated.
          */
          onDecline={() => setExplainingNearby(false)}
        />
      )}

      {error && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}

      {/* A signed-in donor's promises and the way to pay each (GH-809). Absent when there are
          none, and for everybody who is not signed in. */}
      {donorToken && donorSession && <DonorPledges token={donorToken} />}

      {/*
        Split, the heading names what is under it; whole, it is the tier label — which query
        answered — exactly as before. The radius note rides on whichever heading is *first*: it is
        one fact about the whole list, and printing it over both halves would read as two radii.
      */}
      {eventsHidden && <EventsForMembers />}

      <h2 className="home__section">{isSplit ? "Events" : heading}</h2>
      {headingNote && <p className="home__section-note">{headingNote}</p>}

      {busy && results.length === 0 && <p className="notice">Loading…</p>}

      {!busy && results.length === 0 && (
        <Explain className="notice" label="Nothing matched that" title="Finding an organization">
          <p>Try a city name, or scan the code at the door.</p>
        </Explain>
      )}

      <ul className="results">
        {(isSplit ? happening : visible).map((entry) => (
          <ResultRow key={entry.id} entry={entry} />
        ))}
      </ul>

      {/*
        Upcoming (GH-800 on iOS, GH-809 here): announced events near the donor, by date, never
        live. Signed in and with a position only — the owner's rule — and not while a search is
        answering, where the list is about the words typed rather than about here.
      */}
      {donorSession && position && mode !== "search" && <UpcomingEvents position={position} />}

      {isSplit && showOrganizations && (
        <>
          {/*
            Named for what they are rather than for what they lack. "No events" would be a list of
            disappointments; these are the organizations a donor can still find, recognise, give to
            — every approved one has a general fund — and come back to when something is running.
            Drawn only once asked for (2026-09-16); before that the events are the whole screen.
          */}
          <h2 className="home__section home__section--next">Organizations</h2>
          <ul className="results">
            {organizations.map((entry) => (
              <ResultRow key={entry.id} entry={entry} />
            ))}
          </ul>
        </>
      )}

      {(hidesOrganizations || hasMore || hiddenCount > 0) && (
        <button
          type="button"
          className="home__more"
          disabled={busy}
          onClick={() => {
            // First, and whatever else the press does: the organizations already fetched come
            // out from behind the events. One button doing every kind of "more" on purpose.
            setShowOrganizations(true);
            if (hidesOrganizations && !hasMore && hiddenCount === 0) return;
            if (query.trim()) {
              // Search already returns a long list; more means the whole directory view.
              setQuery("");
              void load(position, SHOW_MORE_COUNT);
              return;
            }
            if (mode === "nearby") {
              // One "Show more" doing two jobs on purpose: it widens the ~1 mile radius
              // (revealing the already-fetched rows beyond it) and, when the server says more
              // rows exist, fetches the full distance-sorted list. Splitting those into two
              // controls would ask the donor to care about a paging boundary they cannot see.
              setExpanded(true);
              if (hasMore) void load(position, MAX_DIRECTORY_RESULTS);
              return;
            }
            void load(position, SHOW_MORE_COUNT);
          }}
        >
          Show more
        </button>
      )}

      {panel !== null && (
        <DonorPanel
          token={donorToken}
          session={donorSession}
          view={panel}
          joinEventId={joinEventId}
          // The directory's stranger control reads "Check in": its picker is what it promised.
          // The invite card's "Have an account? Sign in" promised the sign-in door (GH-871b).
          strangerStartsAt={signInDirect ? "sign-in" : "pick-event"}
          onJoinConsumed={() => setJoinEventId(null)}
          entries={results}
          onSession={(token, session) => {
            setDonorToken(token);
            setDonorSession(session);
          }}
          onSignedOut={() => {
            setDonorToken(null);
            setDonorSession(null);
            setPanel(null);
          }}
          onDismiss={() => {
            setPanel(null);
            setSignInDirect(false);
          }}
        />
      )}
    </main>
  );
}

/**
 * One organization.
 *
 * An organization with nowhere to give is still listed — it is discoverable, which is the point of
 * a directory — but it is not dressed up as a button that goes nowhere. `canAcceptDonations` and
 * a live event are different conditions with different remedies, and the row says which is
 * missing rather than collapsing both into a dead tap.
 */
function ResultRow({ entry }: { entry: DirectoryEntry }) {
  const givable = entry.canAcceptDonations && entry.liveEventId !== null;
  // Whether to *name* an event on this row, which is a narrower question than whether a gift can
  // be sent: the general fund is a destination, not an occasion, and "General fund" repeated down
  // a directory is a line that distinguishes nothing. See `isHappening`.
  const onNow = isHappening(entry);

  const body = (
    <>
      <OrgMark
        displayName={entry.displayName}
        logoUrl={entry.logoUrl}
        className="result__mark"
        size={44}
        loading="lazy"
      />

      <span className="result__text">
        {onNow ? (
          // A row with something on is headlined by the event, with the organization and its
          // locality on the line under it — the way the iOS row has read it since 2026-08-22,
          // and the way a row that *opens the event* (2026-09-16) ought to read (UX review
          // 2026-09-17). Muted when giving is not open yet, so the row reads as "join" rather
          // than "give". The venue address rides under when the organizer set one: the event may
          // not be at the organization's own building, and the address is what tells a donor
          // where to actually walk.
          <>
            <span
              className={givable ? "result__name" : "result__name result__event--muted"}
              dir="auto"
            >
              {entry.liveEventName}
            </span>
            <span className="result__meta">
              <span dir="auto">{entry.displayName}</span>
              {entry.locality ? ` · ${entry.locality}` : ""}
            </span>
            {entry.liveEventAddress && (
              <span className="result__meta">{entry.liveEventAddress}</span>
            )}
          </>
        ) : (
          <>
            <span className="result__name" dir="auto">
              {entry.displayName}
            </span>
            {/*
              Locality only. `distanceLabel` is still on the wire and is deliberately not read:
              the server keeps computing a coarse label and this client stops drawing it, so the
              tier sentence above carries the whole of what the donor learns about proximity.
            */}
            <span className="result__meta">{entry.locality}</span>
          </>
        )}
        {onNow ? null : !entry.canAcceptDonations ? (
          <span className="result__event result__event--muted">{entry.verificationLabel}</span>
        ) : entry.liveEventId === null ? (
          <span className="result__event result__event--muted">Nothing running right now</span>
        ) : // Approved, with a general fund and nothing on. Deliberately silent rather than
        // "Nothing running right now": the row *can* take a gift, and a line saying nothing is
        // running reads, next to a name under a heading called Organizations, as a closed door.
        null}
      </span>
    </>
  );

  // A row for an event that is on opens **the event** (owner-directed 2026-09-16); every other
  // row opens the organization's own page, including a row nothing is running at.
  //
  // Every row used to open the organization, even one headlined by tonight's event, and the big
  // button on that page mints the general fund — so a donor who tapped an event was a page and a
  // "See what's happening" away from the thing they tapped, with a Give in between that went
  // somewhere else. `isHappening` rather than `liveEventId !== null`: the general fund is somewhere
  // to give, not somewhere to go, and its row still opens the organization.
  //
  // Still a plain href either way: it works with JavaScript stalled, in a reader view, and on
  // middle click, and it asks the server for nothing until the donor presses Give.
  const href =
    onNow && entry.liveEventId !== null
      ? `/e/${encodeURIComponent(entry.liveEventId)}`
      : `/o/${encodeURIComponent(entry.id)}`;
  return (
    <li className={entry.canAcceptDonations ? "result" : "result result--inactive"}>
      <a className="result__inner" href={href}>
        {body}
      </a>
    </li>
  );
}
